import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { REFRESH_TOKEN_DURATION_MS, AUTH0_SESSION_REQUIRE_LOGOUT_SEC } from '@core/auth/auth.const';
import { AuthService } from '@core/auth/auth.service';
import { LogoutWarningModalComponent } from '@shared/logout-warning-modal';
import { CountdownClockModal, CountdownClock } from '@shared/logout-warning-modal/countdown-clock';
import { timer } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SessionValidityService {
  private readonly checkSessionValidityIntervalMs = 15 * 1000;
  private readonly mandatoryWarningModalDisplayTimeLengthSec = 60;
  private readonly minimumTimeToTokenDeactivationMs = 60 * 1000 + REFRESH_TOKEN_DURATION_MS;
  private readonly sessionTimeAfterMandatoryLogoutWarningAppearSec =
    AUTH0_SESSION_REQUIRE_LOGOUT_SEC - this.mandatoryWarningModalDisplayTimeLengthSec;
  private idleWarningShown: boolean;
  private mandatoryLogoutWarningShown: boolean;

  constructor(private authService: AuthService, private dialog: MatDialog) {}

  checkSessionValidity(): void {
    timer(1000, this.checkSessionValidityIntervalMs)
      .pipe(withLatestFrom(this.authService.isAuthenticated$))
      .subscribe(([, isAuthenticated]) => {
        if (!isAuthenticated) {
          this.authService.logout();
        } else {
          this.runIdleWarningLogic();
          this.runMandatoryWarningLogic();
        }
      });
  }

  private runIdleWarningLogic(): void {
    if (this.shouldShowIdleWarning()) {
      this.showIdleWarning();
    }
  }

  shouldShowIdleWarning(): boolean {
    return !this.idleWarningShown && this.isNoTimeLeftForIdleWarning();
  }

  private isNoTimeLeftForIdleWarning(): boolean {
    return (
      this.authService.timeToTokenDeactivationFromNowMs < this.minimumTimeToTokenDeactivationMs
    );
  }

  private showIdleWarning(): void {
    this.idleWarningShown = true;
    this.dialog.open<LogoutWarningModalComponent, CountdownClockModal>(
      LogoutWarningModalComponent,
      this.buildIdleModalData(),
    );
  }

  private runMandatoryWarningLogic() {
    if (this.mandatoryLogoutWarningShown || !this.authService.loginTime) {
      return;
    }
    if (
      this.authService.secondsSinceLogin >= this.sessionTimeAfterMandatoryLogoutWarningAppearSec
    ) {
      this.openMandatoryLogoutWarning();
    }
  }

  private openMandatoryLogoutWarning() {
    this.mandatoryLogoutWarningShown = true;
    this.dialog
      .open<LogoutWarningModalComponent, CountdownClockModal>(
        LogoutWarningModalComponent,
        this.buildMandatoryModalData(),
      )
      .afterClosed()
      .subscribe((countdownClock: CountdownClock) =>
        this.finishCountingAfterModalClose(countdownClock),
      );
  }

  private finishCountingAfterModalClose(countdownClock: CountdownClock) {
    setInterval(() => {
      countdownClock.current++;
      countdownClock.counter--;
      if (countdownClock.current >= countdownClock.from) {
        this.authService.logout();
      }
    }, 1000);
  }

  private buildIdleModalData() {
    return {
      disableClose: true,
      data: {
        title: 'You’ve been idle for a while, the system will log you out in:',
        from: this.authService.secondsLeftInSession,
        buttonCallback: async () => {
          await this.authService.renewToken(true);
          this.idleWarningShown = false;
        },
        logoutCallback: () => this.authService.logout(),
        resumable: true,
      },
    };
  }

  private buildMandatoryModalData() {
    return {
      disableClose: true,
      data: {
        title: 'Warning! For security reasons, mandatory log out of this session in:',
        subtitle: 'Please save your work in progress immediately, and log back in to continue.',
        from: AUTH0_SESSION_REQUIRE_LOGOUT_SEC - this.authService.secondsSinceLogin,
        logoutCallback: () => this.authService.logout(),
        resumable: false,
      },
    };
  }
}
